import { openOutput } from "./fuxi-audio.js";

export type PlaybackState = "loading" | "playing" | "ended" | "unavailable";

/** Play one checked utterance at a time, including browsers that need a user gesture. */
export class SentencePlayback {
  #output: AudioContext | undefined;
  #source: AudioBufferSourceNode | undefined;
  #cancel: (() => void) | undefined;
  #generation = 0;

  constructor(
    private readonly properties: {
      readonly changed: (state: PlaybackState) => void;
      readonly open?: () => AudioContext | undefined;
      readonly fetch?: typeof fetch;
      readonly timeoutMs?: number;
    },
  ) {}

  /** A replay cancels the previous request and checks the recording bytes again. */
  async play(address: string, sha256: string): Promise<void> {
    this.stop();
    const generation = this.#generation;
    const current = (): boolean => generation === this.#generation;
    const controller = new AbortController();
    this.#cancel = (): void => controller.abort();
    this.properties.changed("loading");
    this.#output ??= (this.properties.open ?? openOutput)();
    const output = this.#output;
    if (output === undefined) {
      this.properties.changed("unavailable");
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      // Resume inside the click before waiting for the network.
      const awake =
        output.state === "running" ? Promise.resolve() : output.resume();
      const samples = async (): Promise<AudioBuffer> => {
        const response = await (this.properties.fetch ?? fetch)(address, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("Recording unavailable");
        }
        const bytes = await response.arrayBuffer();
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        const actual = [...new Uint8Array(digest)]
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
        if (actual !== sha256) {
          throw new Error("Recording changed since review");
        }
        return output.decodeAudioData(bytes);
      };
      const [buffer] = await Promise.race([
        Promise.all([samples(), awake]),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error("Playback timed out"));
          }, this.properties.timeoutMs ?? 12000);
        }),
      ]);
      if (!current()) {
        return;
      }
      if (output.state !== "running" || buffer.duration <= 0) {
        throw new Error("Playback restricted");
      }
      const source = output.createBufferSource();
      source.buffer = buffer;
      source.connect(output.destination);
      source.addEventListener(
        "ended",
        (): void => {
          if (current()) {
            this.#source = undefined;
            source.disconnect();
            this.properties.changed("ended");
          }
        },
        { once: true, signal: controller.signal },
      );
      this.#source = source;
      output.addEventListener(
        "statechange",
        (): void => {
          if (
            current() &&
            this.#source !== undefined &&
            output.state !== "running"
          ) {
            this.stop();
            this.properties.changed("unavailable");
          }
        },
        { signal: controller.signal },
      );
      source.start();
      this.properties.changed("playing");
    } catch {
      if (current()) {
        this.stop();
        this.properties.changed("unavailable");
      }
    } finally {
      clearTimeout(timer);
    }
  }

  /** Stop pending and audible playback when the question changes or text is requested. */
  stop(): void {
    this.#generation += 1;
    this.#cancel?.();
    this.#cancel = undefined;
    if (this.#source !== undefined) {
      this.#source.stop();
      this.#source.disconnect();
      this.#source = undefined;
    }
  }
}
